import React, { useEffect, useRef, useState } from 'react'
import o from '../../components/Overlay.module.css'
import { apiJson } from '../../lib/api'
import { useChanged, useLayer } from '../../components/layers'
import { PendingButton, inertClass, useSlow, Failed } from '../../components/pending'

export interface TeamsSettings {
  bin_counter: boolean
  bin_size: number | null
  add_weight: boolean
  add_weight_bins: boolean
  // Shared bins in Self Scan (198). An older API leaves it out, and it is then not sent.
  big_bins?: boolean
}

// The empty field is no size; anything else must be a whole number from 1 to 9999.
export function binSizeOf(text: string): number | null | 'bad' {
  const t = text.trim()
  if (!t) return null
  if (!/^\d+$/.test(t)) return 'bad'
  const n = Number(t)
  return n >= 1 && n <= 9999 ? n : 'bad'
}

// The Teams cog: settings that apply to every team of the client. For now the Bin Counter on Dataphyll Crew's
// Receipting screen: a running count of the items receipted into the bin in front of the receiver, with a Reset.
// The count stays on the phone; it is never uploaded. And Add weight to bin: the receiver scans an item's tag and types
// its weight, which Control takes as a scale's weighing of that item.
export function TeamsSettingsModal({ user, onClose, onSaved }: { user: any; onClose: () => void; onSaved: (message: string) => void }) {
  const [loaded, setLoaded] = useState<TeamsSettings | null>(null)
  const [on, setOn] = useState(false)
  const [size, setSize] = useState('')
  const [weigh, setWeigh] = useState(false)
  const [weighBins, setWeighBins] = useState(false)
  const [bigBins, setBigBins] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const panelEl = useRef<HTMLDivElement>(null)
  const dirty = useChanged({ on, size, weigh, weighBins, bigBins }, !!loaded)
  const { requestClose, confirm } = useLayer(true, onClose, { dirty: dirty && !busy, el: panelEl, modal: true, focusFirst: true, busy })
  const slow = useSlow(busy)

  useEffect(() => {
    let live = true
    apiJson<TeamsSettings>(user, '/api/teams/settings')
      .then((s) => {
        if (!live) return
        setOn(s.bin_counter)
        setSize(s.bin_size === null ? '' : String(s.bin_size))
        setWeigh(!!s.add_weight)
        setWeighBins(!!s.add_weight_bins)
        setBigBins(!!s.big_bins)
        setLoaded(s)
      })
      .catch((e: any) => live && setError(e?.message || 'Could not load the team settings'))
    return () => {
      live = false
    }
  }, [user])

  const parsed = binSizeOf(size)
  const save = async () => {
    if (parsed === 'bad') {
      setError('Items per bin must be a whole number from 1 to 9999, or left empty.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      const body = { bin_counter: on, bin_size: parsed, add_weight: weigh, add_weight_bins: weighBins, ...(loaded?.big_bins === undefined ? {} : { big_bins: bigBins }) }
      const s = await apiJson<TeamsSettings>(user, '/api/teams/settings', { method: 'PUT', body })
      const onlyBigBins =
        loaded && !!loaded.big_bins !== !!s.big_bins && loaded.add_weight === s.add_weight && loaded.add_weight_bins === s.add_weight_bins && loaded.bin_counter === s.bin_counter && loaded.bin_size === s.bin_size
      onSaved(
        onlyBigBins
          ? `Shared bins in Self Scan turned ${s.big_bins ? 'on' : 'off'}`
          : loaded && loaded.add_weight !== s.add_weight && loaded.add_weight_bins === s.add_weight_bins && loaded.bin_counter === s.bin_counter && loaded.bin_size === s.bin_size
          ? `Item tags ${s.add_weight ? 'on' : 'off'} for Add weight to item`
          : loaded && loaded.add_weight_bins !== s.add_weight_bins && loaded.add_weight === s.add_weight && loaded.bin_counter === s.bin_counter && loaded.bin_size === s.bin_size
          ? `Bin tags ${s.add_weight_bins ? 'on' : 'off'} for Add weight to item`
          : !s.bin_counter
            ? 'Bin Counter on Receipting turned off'
            : s.bin_size
              ? `Bin Counter on Receipting: ${s.bin_size} items per bin`
              : 'Bin Counter on Receipting turned on',
      )
    } catch (e: any) {
      setError(e?.message || 'Could not save the team settings')
      setBusy(false)
    }
  }

  return (
    <div className={o.scrim} onClick={requestClose}>
      <div ref={panelEl} className={[o.modal, o.narrow].join(' ')} role="dialog" aria-modal="true" aria-label="Team settings" onClick={(e) => e.stopPropagation()}>
        <div className={o.head}>
          <div className={o.headText}>
            <h2 className={o.title}>Team settings</h2>
            <p className={o.sub}>These apply to every team in this subscription.</p>
          </div>
          <button type="button" className={o.close} aria-label="Close" onClick={requestClose}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={`${o.body} ${inertClass(busy, slow)}`}>
          {!loaded && !error && <p className={o.fieldHint}>Loading…</p>}
          {loaded && (
            <>
              <div className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>Bin Counter on Receipting</div>
                  <div className={o.toggleSub}>
                    Dataphyll Crew’s Receipting screen counts the items receipted into the current bin, with a Reset for the next one. The count stays on the phone and is not uploaded.
                  </div>
                </div>
                <button type="button" role="switch" aria-checked={on} aria-label="Bin Counter on Receipting" className={o.switch} onClick={() => setOn(!on)} />
              </div>
              {on && (
                <div className={o.field}>
                  <label className={o.fieldLabel} htmlFor="teams-bin-size">
                    Items per bin (optional)
                  </label>
                  <input
                    id="teams-bin-size"
                    className={o.input}
                    inputMode="numeric"
                    placeholder="Not set"
                    value={size}
                    aria-invalid={parsed === 'bad'}
                    onChange={(e) => setSize(e.target.value.replace(/[^0-9]/g, ''))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') save()
                    }}
                  />
                  <div className={o.fieldHint}>
                    {parsed === null
                      ? 'Left empty, the phone shows just the count, e.g. 12.'
                      : parsed === 'bad'
                        ? 'A whole number from 1 to 9999.'
                        : `The phone shows e.g. 12 / ${parsed}, and warns once the bin passes ${parsed}.`}
                  </div>
                </div>
              )}
              <div className={o.toggleText}>
                <div className={o.toggleTitle}>Add weight to item</div>
                <div className={o.toggleSub}>
                  Dataphyll Crew’s Receipting screen gets Add weight to item: scan a tag and type its weight in kg. Control records it as a weighing, as a Field scale would, marked as from Crew.
                </div>
              </div>
              <div className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>Allow item tags</div>
                  <div className={o.toggleSub}>The weight is the item’s.</div>
                </div>
                <button type="button" role="switch" aria-checked={weigh} aria-label="Allow item tags" className={o.switch} onClick={() => setWeigh(!weigh)} />
              </div>
              <div className={o.toggleRow}>
                <div className={o.toggleText}>
                  <div className={o.toggleTitle}>Allow bin tags</div>
                  <div className={o.toggleSub}>
                    A bin tag picked or receipted as an item is weighed as that item. Otherwise the weight goes on the latest batch receipted into that bin today or yesterday, shared pro-rata across its items.
                  </div>
                </div>
                <button type="button" role="switch" aria-checked={weighBins} aria-label="Allow bin tags" className={o.switch} onClick={() => setWeighBins(!weighBins)} />
              </div>
              {loaded.big_bins !== undefined && (
                <div className={o.toggleRow}>
                  <div className={o.toggleText}>
                    <div className={o.toggleTitle}>Shared bins in Self Scan</div>
                    <div className={o.toggleSub}>
                      Pickers in Dataphyll Crew’s Self Scan may scan a bin’s tag. Everyone who scans it within 4 hours of the first scan, or until the bin is receipted, shares it equally: the
                      bin’s weight once weighed, until then its tag’s full weight or the Site’s estimated bin weight. A person counts once per bin.
                    </div>
                  </div>
                  <button type="button" role="switch" aria-checked={bigBins} aria-label="Shared bins in Self Scan" className={o.switch} onClick={() => setBigBins(!bigBins)} />
                </div>
              )}
            </>
          )}
        </div>
        <div className={o.foot}>
          <button type="button" className={o.ghost} disabled={busy} onClick={requestClose}>
            Cancel
          </button>
          <PendingButton className={o.primary} busy={busy} pending="Saving…" onClick={save} disabled={!loaded}>
            Save settings
          </PendingButton>
          <Failed text={error} />
        </div>
      </div>
      {confirm}
    </div>
  )
}
